import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";
import * as Sentry from "@sentry/react";

export interface ApiResponse<T = any> {
  data: T;
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

/**
 * Forms initialise optional fields to "" and post them as-is. An empty string is
 * a *present* value, so `@IsOptional()` does not skip it and format validators
 * (`@IsEmail`, `@IsDateString`) reject the whole request — an optional field ends
 * up behaving like a required one.
 *
 * Normalising "" to null fixes that: `@IsOptional()` skips null, while TypeORM
 * still writes NULL, so "clear this field" on an update keeps working. Fields
 * that really are mandatory still fail, because `@IsNotEmpty()` rejects null.
 */
function normalizeEmptyStrings(value: any): any {
  if (value === "") {
    return null;
  }

  if (Array.isArray(value)) {
    return value.map(normalizeEmptyStrings);
  }

  // Only walk plain objects. FormData, Blob/File and Date must pass through
  // untouched or the payload is destroyed.
  if (value === null || typeof value !== "object") {
    return value;
  }
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) {
    return value;
  }

  const result: Record<string, any> = {};
  for (const [key, entry] of Object.entries(value)) {
    result[key] = normalizeEmptyStrings(entry);
  }
  return result;
}

// Notification manager interface
interface NotificationManager {
  showLoading: (message: string) => string;
  showSuccess: (message: string, duration?: number) => string;
  showError: (message: string, duration?: number) => string;
  hideNotification: (id: string) => void;
}

class ApiService {
  private axiosInstance: AxiosInstance;
  private baseUrl: string;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
  }> = [];
  private onAuthFailure?: () => void;
  private notificationManager?: NotificationManager;

  constructor() {
    // const envBase = ((import.meta as any)?.env?.VITE_API_BASE_URL);
    // this.baseUrl = envBase || '';
    // try {
    //   // eslint-disable-next-line no-console
    //   console.log('[ApiService] VITE_API_BASE_URL from env =', envBase);
    //   // eslint-disable-next-line no-console
    //   console.log('[ApiService] Using API baseURL =', this.baseUrl);
    // } catch {}
    // if (!this.baseUrl) {
    //   // Fail fast if env is missing to avoid using wrong origin
    //   throw new Error('VITE_API_BASE_URL is not defined. Ensure frontend/.env(.<mode>) contains VITE_API_BASE_URL and restart the dev server.');
    // }

    // Type workaround to silence TS error since 'env' is not on ImportMeta by default
    // See vite docs: https://vitejs.dev/guide/env-and-mode.html
    this.baseUrl = (import.meta as any).env?.VITE_API_BASE_URL;

    this.axiosInstance = axios.create({
      baseURL: this.baseUrl,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
      timeout: 30000, // 30 seconds timeout
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add the auth token.
    this.axiosInstance.interceptors.request.use(
      config => {
        const token = localStorage.getItem("auth_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (config.data instanceof FormData) {
          // Preserve the file; let the browser supply the multipart boundary.
          config.headers.delete("Content-Type");
        } else if (
          config.data &&
          typeof config.data !== "string"
        ) {
          config.data = normalizeEmptyStrings(config.data);
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for token refresh
    this.axiosInstance.interceptors.response.use(
      response => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, queue the request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.axiosInstance(originalRequest);
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshToken = localStorage.getItem("auth_refresh_token");
            if (!refreshToken) {
              console.log("❌ No refresh token available, clearing auth and breaking retry loop");
              this.clearAuth();
              this.processQueue(new Error("No refresh token available"));
              // Don't retry the request - break the loop
              return Promise.reject(new Error("Authentication required - please login"));
            }

            console.log("🔄 Token expired, attempting refresh...");
            const response = await this.refreshToken(refreshToken);

            // /auth/refresh returns { access_token, refresh_token } with no `success` flag.
            if (response?.access_token) {
              // Update tokens in localStorage
              localStorage.setItem("auth_token", response.access_token);
              localStorage.setItem("auth_refresh_token", response.refresh_token);

              // Update the authorization header
              this.axiosInstance.defaults.headers.common[
                "Authorization"
              ] = `Bearer ${response.access_token}`;
              originalRequest.headers["Authorization"] = `Bearer ${response.access_token}`;

              console.log("✅ Token refreshed successfully");

              // Process failed queue
              this.processQueue(null);

              // Retry the original request
              return this.axiosInstance(originalRequest);
            } else {
              throw new Error("Token refresh failed");
            }
          } catch (refreshError) {
            console.error("❌ Token refresh failed:", refreshError);
            this.clearAuth();
            this.processQueue(refreshError);
            // Don't retry the request - break the loop
            return Promise.reject(new Error("Authentication failed - please login"));
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async refreshToken(refreshToken: string) {
    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        // The API expects camelCase; `refresh_token` was stripped by its validator, so refresh always failed.
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  private processQueue(error: any) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    });
    this.failedQueue = [];
  }

  private clearAuth() {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_refresh_token");
    localStorage.removeItem("auth_user");
    delete this.axiosInstance.defaults.headers.common["Authorization"];

    // Trigger auth failure callback if set
    if (this.onAuthFailure) {
      this.onAuthFailure();
    } else {
      // Fallback: reload page
      window.location.reload();
    }
  }

  // Method to set callback for authentication failures
  setAuthFailureCallback(callback: () => void) {
    this.onAuthFailure = callback;
  }

  // Method to set notification manager
  setNotificationManager(manager: NotificationManager) {
    this.notificationManager = manager;
  }

  // Generic request method
  async request<T = any>(config: {
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    url: string;
    data?: any;
    params?: any;
    headers?: Record<string, string>;
    responseType?: "json" | "blob";
    signal?: AbortSignal;
    /** Overrides the 30-second default for slow calls (AI features); post() passes it through. */
    timeout?: number;
    showNotifications?: boolean; // Option to disable notifications for specific requests
    showLoading?: boolean; // Explicitly opt-in to loading notification (default false)
  }): Promise<T> {
    const isWriteOperation = config.method !== "GET";
    const showNotifications = config.showNotifications !== false;
    let loadingId: string | null = null;

    try {
      // Only show loading if explicitly requested on mutations (never on GET)
      if (config.showLoading && showNotifications && this.notificationManager && isWriteOperation) {
        const actionName = this.getActionName(config.method, config.url);
        loadingId = this.notificationManager.showLoading(actionName);
      }

      const response: AxiosResponse<T> = await this.axiosInstance.request({
        method: config.method,
        url: config.url,
        data: config.data,
        params: config.params,
        headers: config.headers,
        responseType: config.responseType,
        signal: config.signal,
        ...(config.timeout ? { timeout: config.timeout } : {}),
      });

      // A misrouted API request can return the frontend/server HTML document
      // with HTTP 200. Do not let that document flow into clinical UI fields.
      if (config.responseType !== "blob" && typeof response.data === "string") {
        const contentType = String(response.headers?.["content-type"] || "").toLowerCase();
        const bodyStart = response.data.trimStart().slice(0, 80).toLowerCase();
        if (contentType.includes("text/html") || /^<(?:!doctype\s+html|html|head|body)\b/.test(bodyStart)) {
          throw new Error("The server returned a web page instead of API data. Please reload and try again.");
        }
      }

      // Show success notification for write operations (POST, PUT, DELETE, PATCH)
      if (isWriteOperation && showNotifications && this.notificationManager) {
        const actionName = this.getActionName(config.method, config.url);
        this.notificationManager.showSuccess(`${actionName} completed successfully`, 3000);
      } else if (loadingId && this.notificationManager) {
        this.notificationManager.hideNotification(loadingId);
      }

      return response.data as T;
    } catch (error: any) {
      // Show error notification for failed requests
      if (showNotifications && this.notificationManager && !axios.isCancel(error)) {
        const actionName = this.getActionName(config.method, config.url);
        const errorMessage = this.getErrorMessage(error);
        this.notificationManager.showError(`${actionName} failed: ${errorMessage}`, 4000);
      }

      Sentry.captureException(error, {
        tags: { method: config.method, url: config.url },
      });

      console.error(`❌ ${config.method} ${config.url} failed:`, error);
      throw error;
    }
  }

  // Helper to get user-friendly action name
  private getActionName(method: string, url: string): string {
    // Remove query strings and fragments from URL
    const cleanUrl = url.split("?")[0].split("#")[0];
    const urlParts = cleanUrl.split("/").filter(Boolean);
    const resource = urlParts[urlParts.length - 1] || urlParts[urlParts.length - 2] || "data";

    const actionMap: Record<string, string> = {
      GET: "Fetching",
      POST: "Creating",
      PUT: "Updating",
      PATCH: "Updating",
      DELETE: "Deleting",
    };

    const action = actionMap[method] || "Processing";
    return `${action} ${this.formatResourceName(resource)}`;
  }

  // Helper to format resource name
  private formatResourceName(resource: string): string {
    // Convert kebab-case or snake_case to Title Case
    return resource.replace(/[-_]/g, " ").replace(/\b\w/g, char => char.toUpperCase());
  }

  // Helper to extract error message
  private getErrorMessage(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return "An error occurred";
  }

  // Convenience methods
  async getBlob(url: string): Promise<Blob> {
    return this.request<Blob>({ method: "GET", url, responseType: "blob", showNotifications: false });
  }

  async get<T = any>(url: string, params?: any, showNotifications?: boolean): Promise<T> {
    return this.request<T>({ method: "GET", url, params, showNotifications });
  }

  async post<T = any>(url: string, data?: any, showNotifications?: boolean, options?: { timeout?: number }): Promise<T> {
    return this.request<T>({ method: "POST", url, data, showNotifications, ...options });
  }

  async put<T = any>(url: string, data?: any, showNotifications?: boolean): Promise<T> {
    return this.request<T>({ method: "PUT", url, data, showNotifications });
  }

  async delete<T = any>(url: string, showNotifications?: boolean): Promise<T> {
    return this.request<T>({ method: "DELETE", url, showNotifications });
  }

  async patch<T = any>(url: string, data?: any, showNotifications?: boolean): Promise<T> {
    return this.request<T>({ method: "PATCH", url, data, showNotifications });
  }

  // Utility methods
  setToken(token: string) {
    console.log("🔑 Setting authorization token:", token ? "Token set" : "No token");
    this.axiosInstance.defaults.headers.common["Authorization"] = token ? `Bearer ${token}` : "";
  }

  clearToken() {
    console.log("🔑 Clearing authorization token");
    delete this.axiosInstance.defaults.headers.common["Authorization"];
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
